import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Refrigerator,
  CalendarDays,
  Timer as TimerIcon,
  ChefHat,
  AlertTriangle,
  CheckCircle2,
  Bell as BellIcon,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { usePantryStore } from '@/stores/pantry-store';
import { useMealPlanStore } from '@/stores/meal-plan-store';
import { useCookbookStore } from '@/stores/cookbook-store';
import { useCookingStore } from '@/stores/cooking-store';
import { getItemFlags } from '@/components/pantry/pantry-utils';
import type { MealType, PantryItem, Timer } from '@/types';

interface NotificationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MEAL_LABEL_KEYS: Record<MealType, string> = {
  breakfast: 'recipe:mealPlan.breakfast',
  lunch: 'recipe:mealPlan.lunch',
  dinner: 'recipe:mealPlan.dinner',
  snack: 'recipe:mealPlan.snack',
};

function useTodayMeals() {
  const entries = useMealPlanStore((s) => s.entries);
  return React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return entries.filter((entry) => entry.date === today);
  }, [entries]);
}

function useExpiringPantryItems(): { item: PantryItem; flags: ReturnType<typeof getItemFlags> }[] {
  const items = usePantryStore((s) => s.items);
  return React.useMemo(() => {
    const flagged: { item: PantryItem; flags: ReturnType<typeof getItemFlags> }[] = [];
    for (const item of items) {
      const flags = getItemFlags(item);
      if (flags.isExpired || flags.isExpiringSoon) flagged.push({ item, flags });
    }
    // Expired first, then by soonest expiry
    flagged.sort((a, b) => {
      if (a.flags.isExpired && !b.flags.isExpired) return -1;
      if (!a.flags.isExpired && b.flags.isExpired) return 1;
      const aDays = a.flags.daysUntilExpiry ?? Number.POSITIVE_INFINITY;
      const bDays = b.flags.daysUntilExpiry ?? Number.POSITIVE_INFINITY;
      return aDays - bDays;
    });
    return flagged;
  }, [items]);
}

function useCompletedTimers(): Timer[] {
  const timers = useCookingStore((s) => s.timers);
  return React.useMemo(() => timers.filter((timer) => timer.status === 'completed'), [timers]);
}

export function useNotificationCount(): number {
  const expiring = useExpiringPantryItems();
  const todayMeals = useTodayMeals();
  const completedTimers = useCompletedTimers();
  return expiring.length + todayMeals.length + completedTimers.length;
}

export function NotificationsPanel({ open, onOpenChange }: NotificationsPanelProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'recipe']);

  const expiring = useExpiringPantryItems();
  const todayMeals = useTodayMeals();
  const completedTimers = useCompletedTimers();
  const recipes = useCookbookStore((s) => s.recipes);

  const recipeNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const r of recipes) map.set(r.id, r.name);
    return map;
  }, [recipes]);

  const close = () => onOpenChange(false);

  const isEmpty = expiring.length === 0 && todayMeals.length === 0 && completedTimers.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('common:notifications.title')} size="md">
        {isEmpty ? (
          <div
            style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: 'var(--text-tertiary)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <BellIcon style={{ width: '32px', height: '32px', color: 'var(--text-muted)' }} />
            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{t('common:notifications.empty')}</div>
            <div style={{ fontSize: '14px' }}>{t('common:notifications.emptyDesc')}</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', maxHeight: '60vh', overflowY: 'auto' }}>
            {expiring.length > 0 && (
              <Section
                icon={<AlertTriangle style={{ width: '14px', height: '14px' }} />}
                title={t('common:notifications.expiringTitle')}
              >
                {expiring.slice(0, 8).map(({ item, flags }) => {
                  const label = flags.isExpired
                    ? t('common:notifications.expiredLabel')
                    : flags.daysUntilExpiry === 0
                      ? t('common:notifications.expiresToday')
                      : t('common:notifications.expiresInDays', { count: flags.daysUntilExpiry ?? 0 });
                  const bg = flags.isExpired ? 'var(--accent-error-bg)' : 'var(--accent-warning-bg)';
                  const color = flags.isExpired ? 'var(--accent-error-text)' : 'var(--accent-warning-text)';
                  return (
                    <Row
                      key={item.id}
                      icon={<Refrigerator style={{ width: '18px', height: '18px', color: 'var(--text-tertiary)' }} />}
                      title={item.ingredientName}
                      subtitle={
                        <span style={{ background: bg, color, padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: '11px', fontWeight: 700 }}>
                          {label}
                        </span>
                      }
                      onClick={() => {
                        close();
                        navigate('/pantry');
                      }}
                    />
                  );
                })}
              </Section>
            )}

            {todayMeals.length > 0 && (
              <Section
                icon={<CalendarDays style={{ width: '14px', height: '14px' }} />}
                title={t('common:notifications.todayMealsTitle')}
              >
                {todayMeals.map((entry) => {
                  const name = entry.recipeId
                    ? recipeNameById.get(entry.recipeId) ?? ''
                    : entry.customMealName ?? '';
                  const slotLabel = t(MEAL_LABEL_KEYS[entry.mealType] as never) as string;
                  return (
                    <Row
                      key={entry.id}
                      icon={<ChefHat style={{ width: '18px', height: '18px', color: 'var(--text-tertiary)' }} />}
                      title={name || slotLabel}
                      subtitle={<span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{slotLabel}</span>}
                      onClick={() => {
                        close();
                        navigate('/planner');
                      }}
                    />
                  );
                })}
              </Section>
            )}

            {completedTimers.length > 0 && (
              <Section
                icon={<TimerIcon style={{ width: '14px', height: '14px' }} />}
                title={t('common:notifications.timersTitle')}
              >
                {completedTimers.map((timer) => (
                  <Row
                    key={timer.id}
                    icon={<CheckCircle2 style={{ width: '18px', height: '18px', color: 'var(--accent-success-text)' }} />}
                    title={timer.label || t('common:notifications.timersTitle')}
                    subtitle={
                      <span style={{ fontSize: '12px', color: 'var(--accent-success-text)', fontWeight: 700 }}>
                        {t('common:notifications.timersDoneLabel')}
                      </span>
                    }
                    onClick={() => {
                      close();
                      navigate('/cook/session');
                    }}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function Section({ icon, title, children }: SectionProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '11px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-tertiary)',
          marginBottom: '6px',
        }}
      >
        {icon}
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>{children}</div>
    </div>
  );
}

interface RowProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  onClick: () => void;
}

function Row({ icon, title, subtitle, onClick }: RowProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-primary)',
        background: 'var(--bg-primary)',
        cursor: 'pointer',
        textAlign: 'start',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-standard)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--accent-primary-bg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-primary)';
      }}
    >
      {icon}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            {subtitle}
          </div>
        )}
      </div>
    </button>
  );
}
